import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Mic, Play, Pause, Download, Loader2, Lock, Volume2, Wand2 } from "lucide-react";
import { toast } from "sonner";

export default function Generate() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [voices, setVoices] = useState([]);
    const [myVoices, setMyVoices] = useState([]);
    const [voiceId, setVoiceId] = useState("");
    const [text, setText] = useState("");
    const [model, setModel] = useState("eleven_multilingual_v2");
    const [busy, setBusy] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);

    const isPro = user?.subscription_status === "pro";

    useEffect(() => {
        if (!loading && !user) nav("/auth?next=/generate");
    }, [loading, user, nav]);

    useEffect(() => {
        api.get("/voices").then((r) => {
            setVoices(r.data.voices || []);
            if (r.data.voices?.[0]) setVoiceId(r.data.voices[0].voice_id);
        }).catch(() => {});
        if (user) {
            api.get("/voices/mine").then((r) => setMyVoices(r.data.voices || [])).catch(() => {});
        }
    }, [user]);

    const generate = async () => {
        if (!text.trim() || !voiceId) return;
        if (!isPro) { nav("/pricing"); return; }
        setBusy(true);
        setAudioUrl(null);
        try {
            const res = await api.post("/tts", { text: text.trim(), voice_id: voiceId, model_id: model }, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            setAudioUrl(url);
            toast.success("Audio ready");
            setTimeout(() => { audioRef.current?.play(); setPlaying(true); }, 100);
        } catch (e) {
            let msg = e?.response?.data?.detail || e.message;
            try {
                if (e?.response?.data instanceof Blob) {
                    const txt = await e.response.data.text();
                    const j = JSON.parse(txt);
                    msg = j.detail || msg;
                }
            } catch (_err) { /* keep original msg */ }
            toast.error("Generation failed", { description: msg });
        } finally {
            setBusy(false);
        }
    };

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (playing) { audioRef.current.pause(); setPlaying(false); }
        else { audioRef.current.play(); setPlaying(true); }
    };

    const downloadAudio = () => {
        if (!audioUrl) return;
        const a = document.createElement("a");
        a.href = audioUrl;
        a.download = `tts-${Date.now()}.mp3`;
        a.click();
    };

    if (loading || !user) return null;

    return (
        <div className="min-h-screen bg-white">
            <Header />
            <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
                <div className="flex items-start justify-between gap-4 mb-10">
                    <div>
                        <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">AI Studio</p>
                        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" data-testid="generate-title">
                            Generate audio.
                        </h1>
                        <p className="mt-2 text-slate-500">Pick a voice, type any text, get a natural-sounding audio file.</p>
                    </div>
                    {!isPro && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-medium text-amber-700" data-testid="pro-required-badge">
                            <Lock className="w-3 h-3" strokeWidth={2} /> Pro only
                        </span>
                    )}
                </div>

                {!isPro && (
                    <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-5 flex items-start gap-4" data-testid="upgrade-banner">
                        <Wand2 className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.75} />
                        <div className="flex-1">
                            <h3 className="text-sm font-medium text-amber-900">Upgrade to use Audio Generation</h3>
                            <p className="text-sm text-amber-800/80 mt-1">Pro unlocks TTS, voice cloning, and translation with audio. From $5/mo.</p>
                        </div>
                        <button
                            onClick={() => nav("/pricing")}
                            className="inline-flex items-center justify-center rounded-xl bg-amber-600 text-white px-4 py-2 text-sm font-medium hover:bg-amber-700 transition-colors"
                            data-testid="upgrade-now-btn"
                        >
                            Upgrade →
                        </button>
                    </div>
                )}

                <div className="space-y-4">
                    <div>
                        <label className="text-xs font-medium text-slate-500 inline-flex items-center gap-1.5">
                            <Mic className="w-3 h-3" strokeWidth={2} /> Voice
                        </label>
                        <select
                            value={voiceId}
                            onChange={(e) => setVoiceId(e.target.value)}
                            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all bg-white"
                            data-testid="voice-select"
                        >
                            {myVoices.length > 0 && (
                                <optgroup label="My cloned voices">
                                    {myVoices.map((v) => (
                                        <option key={v.voice_id} value={v.voice_id}>{v.name} (custom)</option>
                                    ))}
                                </optgroup>
                            )}
                            <optgroup label="Stock voices">
                                {voices.map((v) => (
                                    <option key={v.voice_id} value={v.voice_id}>
                                        {v.name}{v.labels?.gender ? ` · ${v.labels.gender}` : ""}{v.labels?.accent ? ` · ${v.labels.accent}` : ""}
                                    </option>
                                ))}
                            </optgroup>
                        </select>
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-slate-500">Text</label>
                            <span className="text-xs text-slate-400 font-mono">{text.length} / 5000</span>
                        </div>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value.slice(0, 5000))}
                            placeholder="Type any text in any language. ElevenLabs auto-detects."
                            rows={8}
                            className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-[15px] leading-relaxed focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all resize-none"
                            data-testid="tts-text"
                        />
                    </div>

                    <div>
                        <label className="text-xs font-medium text-slate-500">Model</label>
                        <div className="mt-1 grid grid-cols-2 gap-2">
                            {[
                                { id: "eleven_multilingual_v2", label: "Multilingual v2", sub: "Highest quality · 29 languages" },
                                { id: "eleven_turbo_v2_5", label: "Turbo v2.5", sub: "Fast · low latency" },
                            ].map((m) => (
                                <button
                                    key={m.id}
                                    onClick={() => setModel(m.id)}
                                    className={`rounded-xl border px-4 py-3 text-left transition-all ${model === m.id ? "border-indigo-500 bg-indigo-50/50 ring-2 ring-indigo-500/20" : "border-slate-200 bg-white hover:border-slate-300"}`}
                                    data-testid={`model-${m.id}`}
                                >
                                    <div className="text-sm font-medium text-slate-900">{m.label}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{m.sub}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={generate}
                        disabled={busy || !text.trim() || !voiceId}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 text-white px-4 py-3 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        data-testid="generate-btn"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" strokeWidth={2} />}
                        {busy ? "Generating audio…" : isPro ? "Generate" : "Upgrade & generate"}
                    </button>

                    {audioUrl && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-6 fade-in-up" data-testid="audio-output">
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={togglePlay}
                                    className="w-12 h-12 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 flex items-center justify-center transition-colors"
                                    data-testid="play-audio-btn"
                                >
                                    {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                                </button>
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-slate-900 inline-flex items-center gap-1.5">
                                        <Volume2 className="w-3.5 h-3.5 text-indigo-600" strokeWidth={2} /> Ready to play
                                    </div>
                                    <div className="text-xs text-slate-400 mt-0.5">{voices.find((v) => v.voice_id === voiceId)?.name || myVoices.find((v) => v.voice_id === voiceId)?.name || "Voice"}</div>
                                </div>
                                <button
                                    onClick={downloadAudio}
                                    className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                                    data-testid="download-audio-btn"
                                >
                                    <Download className="w-4 h-4" strokeWidth={1.75} /> MP3
                                </button>
                            </div>
                            <audio
                                ref={audioRef}
                                src={audioUrl}
                                onEnded={() => setPlaying(false)}
                                onPause={() => setPlaying(false)}
                                className="hidden"
                            />
                        </div>
                    )}
                </div>

                <div className="mt-12 pt-8 border-t border-slate-100 text-center text-sm text-slate-500">
                    Want to use your own voice? <button onClick={() => nav("/voices")} className="text-indigo-600 hover:text-indigo-700 font-medium" data-testid="goto-voices-link">Clone a voice →</button>
                </div>
            </main>
        </div>
    );
}
